import { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { models, type IEmbedConfiguration } from 'powerbi-client'
import { useMsal } from '@azure/msal-react'
import { useGetUserIdQuery } from '@/features/user-manager'
import { useLazyGetDashboardsQuery, useGetEmbedTokenQuery } from '../api'
import {
  setDashboardState,
  setLoading,
  setError,
  setHasDashboard,
  selectDashboardState,
  selectDashboardLoading,
  selectDashboardError,
  selectHasDashboard
} from '../redux'
import type { PowerBIError } from '../types'
import { useShowToast } from '@/components/toast'

export const useDashboard = (analysisId: string) => {
  const dispatch = useDispatch()
  const { instance } = useMsal()
  const account = instance.getActiveAccount()
  const { data: userData } = useGetUserIdQuery({ oid: account?.localAccountId || '' })
  const [getDashboards, { data: dashboardsData, isLoading: isDashboardsLoading }] = useLazyGetDashboardsQuery()
  const dashboardState = useSelector(selectDashboardState)
  const isLoading = useSelector(selectDashboardLoading)
  const error = useSelector(selectDashboardError)
  const hasDashboard = useSelector(selectHasDashboard)
  const [embedConfig, setEmbedConfig] = useState<IEmbedConfiguration | null>(null)
  const [dashboardId, setDashboardId] = useState<string>('')
  const [currentVizReportId, setCurrentVizReportId] = useState<string>('')
  const [isOutdated, setIsOutdated] = useState<boolean>(false)
  const intervalRef = useRef<number | null>(null)
  const showToast = useShowToast()

  useEffect(() => {
    if (dashboardsData?.response?.data?.[0]) {
      setDashboardId(dashboardsData.response.data[0].id)
    }
  }, [dashboardsData])

  const { data: embedTokenData, isLoading: isTokenLoading } = useGetEmbedTokenQuery(
    { analysisId, dashboardId },
    { skip: !dashboardId }
  )

  // Function to check for dashboard updates
  const checkForDashboardUpdates = useCallback(async () => {
    if (!analysisId) return

    try {
      const result = await getDashboards({ analysisId }).unwrap()
      const latestDashboard = result?.response?.data?.[0]
      const latestVizReportId = latestDashboard?.dashboardDetails?.[0]?.vizReportId

      // If we have a current vizReportId and it's different from the latest one, show a warning
      if (currentVizReportId && latestVizReportId && currentVizReportId !== latestVizReportId) {
        if (!isOutdated) {
          setIsOutdated(true)
          showToast({
            title: 'Dashboard Update Available',
            description: 'The dashboard has been updated. Please refresh the page to view the latest version.',
            status: 'warning',
            duration: 10000,
            isClosable: true,
            position: 'top'
          })
        }
      }
    } catch (err) {
      console.error('Error checking for dashboard updates:', err)
    }
  }, [analysisId, currentVizReportId, getDashboards, isOutdated, showToast])

  // Set up polling interval for dashboard updates
  useEffect(() => {
    if (currentVizReportId) {
      // Clear existing interval if any
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
      }

      // Set new interval - check every 30 seconds
      intervalRef.current = window.setInterval(() => {
        checkForDashboardUpdates()
      }, 30000)
    }

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [currentVizReportId, checkForDashboardUpdates])

  const fetchDashboardData = useCallback(async () => {
    if (!account?.idToken || !userData?.response.data?.userId) return

    try {
      dispatch(setLoading(true))

      const dashboardResult = await getDashboards({ analysisId }).unwrap()

      if (!dashboardResult?.response?.data?.length) {
        dispatch(setHasDashboard(false))
        return
      }

      const dashboard = dashboardResult.response.data[0]
      const dashboardDetails = dashboard.dashboardDetails?.[0]
      const embedInfo = embedTokenData?.response?.data?.embedInfo

      if (!embedInfo || !dashboardDetails) return

      dispatch(setHasDashboard(true))

      // Store the current vizReportId for future comparison
      setCurrentVizReportId(dashboardDetails.vizReportId)

      setEmbedConfig({
        type: 'report',
        id: dashboardDetails.vizReportId,
        embedUrl: embedInfo.embedUrl,
        accessToken: embedInfo.embedToken,
        tokenType: models.TokenType.Embed,
        permissions: models.Permissions.All,
        settings: {
          panes: {
            filters: {
              expanded: false,
              visible: false
            }
          }
        }
      })

      dispatch(setDashboardState({
        dashboardId: dashboard.id,
        embedUrl: dashboard.vizEmbedUrl || embedInfo.embedUrl,
        reportId: dashboardDetails.vizReportId
      }))
    } catch (err) {
      const error: PowerBIError = {
        message: err instanceof Error ? err.message : 'Failed to fetch dashboard data',
        isError: true
      }
      dispatch(setError(error))
    } finally {
      dispatch(setLoading(false))
    }
  }, [account?.idToken, analysisId, getDashboards, embedTokenData, dispatch, userData?.response.data?.userId])

  return {
    fetchDashboardData,
    embedConfig,
    dashboardState,
    isLoading: isLoading || isDashboardsLoading || isTokenLoading,
    error,
    hasDashboard,
    isOutdated
  }
} 